/* */
import React, { Fragment, useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Formik, Form } from 'formik';

import actions from 'redux/actions';
import Loading from 'components/Loading';
import tracking from 'helpers/tracking';
import NewDesign from 'components/NewDesign';
import { showError } from 'helpers/handleError';
import InvoiceServices from 'services/InvoiceServices';
import { useHistory } from 'react-router';
import { AppRoutes } from 'consts/routesPath';
import { PayViaEnum, StatusInvoiceEnum } from 'consts/data';
import useGetListTransaction from 'hooks/invoices/useGetListTransaction';
import SimpleCaptcha from 'components/SimpleCaptcha';
import classNames from 'classnames';
import useToggleDialog from 'hooks/useToggleDialog';
import DialogUpdateProfile from './DialogUpdateProfile';
import { isString } from 'lodash';
import checkBalanceModel from 'models/check-balance.model';
import { isStrIncludeStr } from 'helpers/string';
import { usePaymentHandler } from 'providers/PaymentProvider';
import { notifySuccess } from 'helpers/handleNotification';

const { purchaseCredit } = actions;

const initialFilters = {
  type: PayViaEnum.CreditCard,
  start: '',
  end: '',

  offset: 0,
  limit: 1,

  //* Bank type
  statuses: [],
  showDeletedFile: false,

  //* Credit type
  afterId: '',
};

const SubmitPurchaseDialog = (props) => {
  //! State
  const { isOpen, toggle, className, packageSelected, isBankTransfer } = props;
  const history = useHistory();
  const { id, amount } = packageSelected;
  const dispatch = useDispatch();
  const formRef = useRef();
  const { shouldOpenByPopup } = usePaymentHandler();

  const isNotEnoughBalance = checkBalanceModel.checkIsNotEnoughCredits();

  const [openUpdateBilling, toggleUpdateBilling, shouldRenderUpdateBilling] = useToggleDialog();

  const [verified, setVerify] = useState(false);
  const { data, loading: loadingPrevTransaction } = useGetListTransaction(
    initialFilters,
    !isBankTransfer
  );

  const prevTransaction = data?.result || [];
  const isFirstTransaction = prevTransaction?.length <= 0;
  const statusPrevTransaction = prevTransaction?.[0]?.status || '';
  const isPrevTransactionSuccess = [StatusInvoiceEnum.Succeeded.toLowerCase()].includes(
    statusPrevTransaction?.toLowerCase()
  );

  const isEnableSubmit =
    isFirstTransaction || isBankTransfer || isPrevTransactionSuccess || verified;

  const isShowCaptcha =
    !isFirstTransaction && !isBankTransfer && !loadingPrevTransaction && !isPrevTransactionSuccess;

  //! Function
  const onSubmit = useCallback(
    (values, { setSubmitting }) => {
      tracking.trackEvent('payment/purchase-credit');
      setSubmitting(true);

      dispatch(
        purchaseCredit({
          packageId: id,
          callbacks: {
            onSuccess: () => {
              if (isNotEnoughBalance) {
                window.location.href = AppRoutes.HomePage;
              }

              setSubmitting(false);
              toggle();
            },
            onFailed: () => {
              setSubmitting(false);
            },
          },
        })
      );
    },
    [packageSelected, isOpen, isNotEnoughBalance]
  );

  const onClickSubmitBankTransfer = async (e) => {
    e.preventDefault();
    try {
      formRef.current.setSubmitting(true);
      const response = await InvoiceServices.addOrder({ amount });
      const orderId = response?.data?.id;
      formRef.current.setSubmitting(false);

      if (isNotEnoughBalance || shouldOpenByPopup) {
        toggle();
        notifySuccess('Your request is in progress! Please check the progress in Transactions tab');
        return;
      }

      history.push(
        `${AppRoutes.InvoiceDetailWithId(orderId)}?type=bank&popup=${
          isNotEnoughBalance ? true : false
        }`
      );
    } catch (error) {
      if (isString(error) && isStrIncludeStr(error, 'update billing')) {
        toggleUpdateBilling();
      }

      formRef.current.setSubmitting(false);
      showError(error);
    }
  };

  const onVerifySuccess = useCallback(() => {
    setVerify(true);
  }, []);

  //! Render
  const titleDialog = isBankTransfer ? 'Bank payment' : 'Credit card';
  const titleContent = isBankTransfer ? (
    <NewDesign.Typography>
      Do you want to create an invoice payment of{' '}
      <b>
        $<NewDesign.NumberFormat value={amount} />
      </b>
      ?
    </NewDesign.Typography>
  ) : (
    <NewDesign.Typography>
      Do you want to charge{' '}
      <b>
        $<NewDesign.NumberFormat value={amount} />
      </b>
      ?
    </NewDesign.Typography>
  );

  return (
    <Fragment>
      {shouldRenderUpdateBilling && (
        <DialogUpdateProfile isOpen={openUpdateBilling} toggle={toggleUpdateBilling} />
      )}

      <Formik innerRef={formRef} initialValues={{}} onSubmit={onSubmit}>
        {({ isSubmitting }) => (
          <Modal
            isOpen={isOpen}
            toggle={toggle}
            className={classNames('component:SubmitPurchaseDialog', className)}
          >
            <Form>
              <ModalHeader toggle={toggle}>{titleDialog}</ModalHeader>
              <ModalBody>
                <NewDesign.Typography>
                  {titleContent}

                  {isShowCaptcha && (
                    <div className='d-flex justify-content-center mt-2'>
                      <SimpleCaptcha onVerifySuccess={onVerifySuccess} />
                    </div>
                  )}
                </NewDesign.Typography>
              </ModalBody>
              <ModalFooter>
                <NewDesign.Button
                  type='submit'
                  isLoading={loadingPrevTransaction || isSubmitting}
                  onClick={isBankTransfer && onClickSubmitBankTransfer}
                  disabled={!isEnableSubmit}
                >
                  {isSubmitting ? <Loading color='primary' /> : 'Submit'}
                </NewDesign.Button>

                <NewDesign.Button type='button' variant='text' onClick={toggle}>
                  Close
                </NewDesign.Button>
              </ModalFooter>
            </Form>
          </Modal>
        )}
      </Formik>
    </Fragment>
  );
};

export default React.memo(SubmitPurchaseDialog);
